/* eslint-disable @typescript-eslint/no-unused-vars */
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {Button, Divider, Menu, MenuItem} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useMemo,
  useState,
} from 'react';

import {AssetHuman} from '../../interfaces/AssetHumanAdmin';
import AccessControl from '../common/AccessControl';
import ModalDraggable from '../common/ModalDraggable';
import EmployeeAssetItemsPurgeEventsButton from '../employees/buttons/EmployeeAssetItemsPurgeEventsButton';
import EmployeeAssetItemsPurgeNodeButton from '../employees/buttons/EmployeeAssetItemsPurgeNodeButton';
import EmployeeItemsPurgeChatEventsButton from '../employees/buttons/EmployeeItemsPurgeChatEventsButton';
import AssetHumanItemUpsert from './AssetHumanItemUpsert';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  pk?: number;
  item?: AssetHuman;
  mode?: 'view' | 'update' | 'chat';
  prefetch?: boolean;
  isAdminSection?: boolean;
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onSubmitted?: () => void;
  onPurged?: () => void;
  onOpenHistory?: () => void;
  onOpen?: () => void;
  onClose?: Function;
}

const AssetHumanItemEditButton = <T extends ComponentType = typeof Button>({
  pk,
  item,
  mode,
  prefetch,
  isAdminSection = false,
  component,
  componentProps,
  children,
  onSubmitted,
  onPurged,
  onOpenHistory,
  onOpen,
  onClose,
}: Props<T>) => {
  const Component = component ?? Button;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
  const menuOpen = !!menuAnchorEl;
  const [isOpened, setIsOpened] = useState(false);
  const ids = useMemo(() => {
    return item?.id ? [item?.id] : [];
  }, [item]);

  const canPurgeChat = useMemo(() => {
    return (
      item?.commtrac_node?.type === 'miner' &&
      !item?.commtrac_node?.wifi_enabled
    );
  }, [item]);

  return (
    <>
      <Component
        {...componentProps}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        {children}
      </Component>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onBackdropClick={() => setMenuAnchorEl(undefined)}
      >
        <MenuItem
          onClick={() => {
            setIsOpened(true);
            setMenuAnchorEl(undefined);
          }}
        >
          <EditIcon fontSize="small" sx={{mr: 1.5}} />
          {/* {`Edit ${item?.commtrac_node?.type === 'miner' ? 'Employee' : 'Asset'}`} */}
          Edit Employee
        </MenuItem>
        <AccessControl permissions={['post::/purge']}>
          <Divider />
          <EmployeeAssetItemsPurgeNodeButton
            ids={ids}
            component={MenuItem}
            onDone={() => {
              setMenuAnchorEl(undefined);
              onSubmitted?.();
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 2}} />
            Purge All
          </EmployeeAssetItemsPurgeNodeButton>

          <EmployeeAssetItemsPurgeEventsButton
            ids={ids}
            component={MenuItem}
            onDone={() => {
              setMenuAnchorEl(undefined);
              onSubmitted?.();
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 2}} />
            Purge Transactions
          </EmployeeAssetItemsPurgeEventsButton>

          {canPurgeChat ? (
            <EmployeeItemsPurgeChatEventsButton
              ids={ids}
              component={MenuItem}
              onDone={() => {
                setMenuAnchorEl(undefined);
                onSubmitted?.();
              }}
            >
              <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 2}} />
              Chat
            </EmployeeItemsPurgeChatEventsButton>
          ) : null}
        </AccessControl>
      </Menu>
      {isOpened ? (
        <ModalDraggable open={isOpened}>
          <AssetHumanItemUpsert
            pk={pk}
            item={item}
            mode={mode}
            isAdminSection={isAdminSection}
            prefetch={prefetch}
            onClose={() => {
              setIsOpened(false);
              onClose?.();
            }}
            onSubmitted={() => {
              onSubmitted?.();
              if (!pk) {
                setIsOpened(false);
                onClose?.();
              }
            }}
            onPurged={onPurged}
            onOpenHistory={onOpenHistory}
          />
        </ModalDraggable>
      ) : null}
    </>
  );
};

export default AssetHumanItemEditButton;
